// src/pages/Admin/LiveStreams/LiveStreamManagement.tsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Radio,
  Play,
  Square,
  Plus,
  Eye,
  Users,
  Heart,
  MessageSquare,
  Calendar,
  Clock,
  Copy,
  ExternalLink,
  AlertCircle,
  RefreshCw,
  Trash2,
  Image,
  Upload
} from 'lucide-react';
import { useToast } from '../../../hooks/useToast';
import liveStreamService, { AGE_RATINGS, type AgeRatingValue } from '../../../services/liveStreamService';
import type { LiveStream, LiveStreamAnalytics } from '../../../services/liveStreamService';

interface StreamFormData {
  title: string;
  description: string;
  scheduledStartTime: string;
  /**
   * Required before a stream can be started.
   *
   * There was no field for this anywhere, and /start refuses an unrated stream because
   * unrated is treated as adults-only - so going live was impossible through the UI and
   * the error gave no hint where to fix it.
   */
  ageRating: AgeRatingValue;
}

const LiveStreamManagement: React.FC = () => {
  const navigate = useNavigate();
  const { showError, showSuccess } = useToast();
  const [loading, setLoading] = useState(true);
  const [activeStreams, setActiveStreams] = useState<LiveStream[]>([]);
  const [upcomingStreams, setUpcomingStreams] = useState<LiveStream[]>([]);
  const [pastStreams, setPastStreams] = useState<LiveStream[]>([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showGoLiveModal, setShowGoLiveModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [analytics, setAnalytics] = useState<LiveStreamAnalytics | null>(null);
  const [deletingStreamId, setDeletingStreamId] = useState<string | null>(null);
  const [streamToDelete, setStreamToDelete] = useState<{ id: string; title: string } | null>(null);
  const [uploadingThumbnailId, setUploadingThumbnailId] = useState<string | null>(null);
  const thumbnailInputRef = useRef<HTMLInputElement>(null);
  const [thumbnailUploadStreamId, setThumbnailUploadStreamId] = useState<string | null>(null);
  
  const [formData, setFormData] = useState<StreamFormData>({
    title: '',
    description: '',
    scheduledStartTime: '',
    // No default that asserts a rating nobody chose. 15 is the middle of the scale and
    // still a deliberate-looking answer; defaulting to U would claim a stream had been
    // judged suitable for children when nobody had looked.
    ageRating: '15'
  });

  const loadStreams = useCallback(async () => {
    try {
      setLoading(true);
      const [liveResponse, upcomingResponse, pastResponse, analyticsData] = await Promise.all([
        liveStreamService.getLiveStreams(0, 10),
        liveStreamService.getUpcomingStreams(0, 10),
        liveStreamService.getPastStreams(0, 50),
        liveStreamService.getLiveStreamAnalytics()
      ]);
      
      setActiveStreams(liveResponse.content || []);
      setUpcomingStreams(upcomingResponse.content || []);
      setPastStreams(pastResponse.content || []);
      setAnalytics(analyticsData);
    } catch (error) {
      console.error('Error loading streams:', error);
      // Don't show error toast on initial load failures - just log it
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStreams();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDeleteStream = (streamId: string, streamTitle: string) => {
    setStreamToDelete({ id: streamId, title: streamTitle });
  };

  const confirmDeleteStream = async () => {
    if (!streamToDelete) return;

    setDeletingStreamId(streamToDelete.id);
    try {
      await liveStreamService.deleteStream(streamToDelete.id);
      showSuccess('Stream deleted successfully');
      setStreamToDelete(null);
      loadStreams();
    } catch (error) {
      console.error('Error deleting stream:', error);
      showError(error instanceof Error ? error.message : 'Failed to delete stream');
    } finally {
      setDeletingStreamId(null);
    }
  };

  const handleThumbnailClick = (streamId: string) => {
    setThumbnailUploadStreamId(streamId);
    thumbnailInputRef.current?.click();
  };

  const handleThumbnailUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !thumbnailUploadStreamId) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      showError('Please select an image file');
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      showError('Thumbnail must be less than 10MB');
      return;
    }

    setUploadingThumbnailId(thumbnailUploadStreamId);
    try {
      await liveStreamService.uploadThumbnail(thumbnailUploadStreamId, file);
      showSuccess('Thumbnail uploaded successfully!');
      loadStreams();
    } catch (error) {
      console.error('Error uploading thumbnail:', error);
      showError(error instanceof Error ? error.message : 'Failed to upload thumbnail');
    } finally {
      setUploadingThumbnailId(null);
      setThumbnailUploadStreamId(null);
      // Reset the input
      if (thumbnailInputRef.current) {
        thumbnailInputRef.current.value = '';
      }
    }
  };

  const handleCreateStream = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.title.trim()) {
      showError('Title is required');
      return;
    }

    setIsSubmitting(true);
    try {
      await liveStreamService.createStream({
        title: formData.title,
        description: formData.description || undefined,
        scheduledStartTime: formData.scheduledStartTime || undefined,
        ageRating: formData.ageRating
      });
      
      showSuccess('Live stream created successfully!');
      setShowCreateModal(false);
      setFormData({ title: '', description: '', scheduledStartTime: '', ageRating: '15' });
      loadStreams();
    } catch (error) {
      console.error('Error creating stream:', error);
      showError('Failed to create stream');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGoLive = async () => {
    if (!formData.title.trim()) {
      showError('Title is required');
      return;
    }

    setIsSubmitting(true);
    try {
      // Create the stream and redirect to Go Live page
      const stream = await liveStreamService.createStream({
        title: formData.title,
        description: formData.description || undefined,
        ageRating: formData.ageRating
      });
      
      setShowGoLiveModal(false);
      setFormData({ title: '', description: '', scheduledStartTime: '', ageRating: '15' });
      
      // Navigate to the Go Live page with the stream
      navigate(`/admin/live-streams/go-live/${stream.id}`);
    } catch (error) {
      console.error('Error creating stream:', error);
      showError('Failed to create stream');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleStartStream = async (streamId: string) => {
    // Navigate to Go Live page for this stream
    navigate(`/admin/live-streams/go-live/${streamId}`);
  };

  const handleEndStream = async (streamId: string) => {
    if (!confirm('Are you sure you want to end this stream?')) return;
    
    try {
      await liveStreamService.endStream(streamId);
      showSuccess('Stream ended successfully');
      loadStreams();
    } catch (error) {
      console.error('Error ending stream:', error);
      showError('Failed to end stream');
    }
  };

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    showSuccess(`${label} copied to clipboard`);
  };

  const getStatusBadge = (status: string) => {
    const statusConfig: Record<string, { bg: string; text: string; label: string }> = {
      LIVE: { bg: 'bg-red-100', text: 'text-red-800', label: '🔴 LIVE' },
      SCHEDULED: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Scheduled' },
      CREATED: { bg: 'bg-gray-100', text: 'text-gray-800', label: 'Ready' },
      ENDED: { bg: 'bg-green-100', text: 'text-green-800', label: 'Ended' },
      FAILED: { bg: 'bg-red-100', text: 'text-red-800', label: 'Failed' },
      CANCELLED: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Cancelled' }
    };
    
    const config = statusConfig[status] || { bg: 'bg-gray-100', text: 'text-gray-800', label: status };
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
        {config.label}
      </span>
    );
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Radio className="h-6 w-6 text-red-500" />
            Live Stream Management
          </h1>
          <p className="mt-1 text-gray-600">Create and manage real-time live streams</p>
        </div>
        
        <div className="flex gap-3">
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
          >
            <Calendar className="h-4 w-4" />
            Schedule Stream
          </button>
          <button
            onClick={() => setShowGoLiveModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors animate-pulse"
          >
            <Radio className="h-4 w-4" />
            Go Live Now
          </button>
        </div>
      </div>

      {/* Analytics Summary Cards */}
      {analytics && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center gap-2 text-gray-500 text-sm mb-1">
              <Radio className="h-4 w-4" />
              Total Streams
            </div>
            <div className="text-2xl font-bold text-gray-900">{analytics.totalStreams}</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center gap-2 text-gray-500 text-sm mb-1">
              <Eye className="h-4 w-4" />
              Total Views
            </div>
            <div className="text-2xl font-bold text-gray-900">{analytics.totalStreamViews.toLocaleString()}</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center gap-2 text-gray-500 text-sm mb-1">
              <Heart className="h-4 w-4" />
              Total Likes
            </div>
            <div className="text-2xl font-bold text-gray-900">{analytics.totalStreamLikes.toLocaleString()}</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center gap-2 text-gray-500 text-sm mb-1">
              <MessageSquare className="h-4 w-4" />
              Total Comments
            </div>
            <div className="text-2xl font-bold text-gray-900">{analytics.totalStreamComments.toLocaleString()}</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center gap-2 text-gray-500 text-sm mb-1">
              <Users className="h-4 w-4" />
              Peak Viewers
            </div>
            <div className="text-2xl font-bold text-gray-900">{analytics.peakViewersRecord.toLocaleString()}</div>
          </div>
        </div>
      )}

      {/* Currently Live Section */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <span className="w-3 h-3 bg-red-500 rounded-full animate-pulse"></span>
            Currently Live
          </h2>
          <button onClick={loadStreams} className="p-2 hover:bg-gray-100 rounded-lg">
            <RefreshCw className="h-4 w-4 text-gray-500" />
          </button>
        </div>
        
        {activeStreams.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <Radio className="h-12 w-12 mx-auto mb-3 text-gray-300" />
            <p>No streams are currently live</p>
            <button
              onClick={() => setShowGoLiveModal(true)}
              className="mt-4 text-red-600 hover:text-red-700 font-medium"
            >
              Start a live stream →
            </button>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {activeStreams.map((stream) => (
              <div key={stream.id} className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-semibold text-gray-900">{stream.title}</h3>
                      {getStatusBadge(stream.status)}
                    </div>
                    {stream.description && (
                      <p className="text-gray-600 mb-3">{stream.description}</p>
                    )}
                    
                    <div className="flex flex-wrap gap-4 text-sm text-gray-500">
                      <span className="flex items-center gap-1">
                        <Eye className="h-4 w-4" /> {stream.currentViewers} watching
                      </span>
                      <span className="flex items-center gap-1">
                        <Users className="h-4 w-4" /> {stream.peakViewers} peak
                      </span>
                      <span className="flex items-center gap-1">
                        <Heart className="h-4 w-4" /> {stream.likeCount}
                      </span>
                      <span className="flex items-center gap-1">
                        <MessageSquare className="h-4 w-4" /> {stream.commentCount}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="h-4 w-4" /> Started {formatDate(stream.actualStartTime)}
                      </span>
                    </div>

                    {/* Stream Info */}
                    {stream.rtmpUrl && (
                      <div className="mt-4 p-3 bg-gray-50 rounded-lg text-sm">
                        <p className="font-medium text-gray-700 mb-2">Stream Credentials:</p>
                        <div className="space-y-1">
                          <div className="flex items-center gap-2">
                            <span className="text-gray-500">RTMP URL:</span>
                            <code className="bg-gray-200 px-2 py-0.5 rounded text-xs">{stream.rtmpUrl}</code>
                            <button 
                              onClick={() => copyToClipboard(stream.rtmpUrl!, 'RTMP URL')}
                              className="p-1 hover:bg-gray-200 rounded"
                            >
                              <Copy className="h-3 w-3" />
                            </button>
                          </div>
                          {stream.streamKey && (
                            <div className="flex items-center gap-2">
                              <span className="text-gray-500">Stream Key:</span>
                              <code className="bg-gray-200 px-2 py-0.5 rounded text-xs">••••••••</code>
                              <button 
                                onClick={() => copyToClipboard(stream.streamKey!, 'Stream Key')}
                                className="p-1 hover:bg-gray-200 rounded"
                              >
                                <Copy className="h-3 w-3" />
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                  
                  <div className="flex gap-2 ml-4">
                    <a
                      href={`/live/${stream.slug}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg"
                      title="View stream"
                    >
                      <ExternalLink className="h-5 w-5" />
                    </a>
                    <button
                      onClick={() => handleEndStream(stream.id)}
                      className="flex items-center gap-2 px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200"
                    >
                      <Square className="h-4 w-4" />
                      End Stream
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Upcoming Streams */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <Calendar className="h-5 w-5 text-blue-500" />
            Scheduled Streams
          </h2>
        </div>
        
        {upcomingStreams.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <Calendar className="h-12 w-12 mx-auto mb-3 text-gray-300" />
            <p>No upcoming streams scheduled</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {upcomingStreams.map((stream) => (
              <div key={stream.id} className="p-4 flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-3 mb-1">
                    <h3 className="font-medium text-gray-900">{stream.title}</h3>
                    {getStatusBadge(stream.status)}
                  </div>
                  <p className="text-sm text-gray-500">
                    Scheduled for {formatDate(stream.scheduledStartTime)}
                  </p>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleStartStream(stream.id)}
                    className="flex items-center gap-2 px-3 py-1.5 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 text-sm"
                  >
                    <Play className="h-4 w-4" />
                    Go Live
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Past Streams */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <Clock className="h-5 w-5 text-gray-500" />
            Past Streams
          </h2>
        </div>
        
        {pastStreams.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <Clock className="h-12 w-12 mx-auto mb-3 text-gray-300" />
            <p>No past streams yet</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Thumbnail</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Title</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Views</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Peak</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Engagement</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Recording</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {pastStreams.map((stream) => (
                  <tr key={stream.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      {stream.thumbnailUrl ? (
                        <div className="relative group">
                          <img 
                            src={stream.thumbnailUrl} 
                            alt={stream.title}
                            className="h-12 w-20 object-cover rounded"
                          />
                          <button
                            onClick={() => handleThumbnailClick(stream.id)}
                            disabled={uploadingThumbnailId === stream.id}
                            className="absolute inset-0 bg-black bg-opacity-50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center rounded"
                          >
                            <Upload className="h-4 w-4 text-white" />
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => handleThumbnailClick(stream.id)}
                          disabled={uploadingThumbnailId === stream.id}
                          className="h-12 w-20 bg-gray-100 hover:bg-gray-200 border-2 border-dashed border-gray-300 rounded flex items-center justify-center transition-colors"
                          title="Upload thumbnail"
                        >
                          {uploadingThumbnailId === stream.id ? (
                            <div className="animate-spin rounded-full h-4 w-4 border-2 border-gray-600 border-t-transparent"></div>
                          ) : (
                            <div className="flex flex-col items-center">
                              <Image className="h-4 w-4 text-gray-400" />
                              <span className="text-[10px] text-gray-400 mt-0.5">Add</span>
                            </div>
                          )}
                        </button>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{stream.title}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(stream.endedAt)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {stream.totalViews.toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {stream.peakViewers.toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <span className="flex items-center gap-3">
                        <span className="flex items-center gap-1">
                          <Heart className="h-3 w-3" /> {stream.likeCount}
                        </span>
                        <span className="flex items-center gap-1">
                          <MessageSquare className="h-3 w-3" /> {stream.commentCount}
                        </span>
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {stream.recordingUrl ? (
                        // The replay page, not the media URL. Linking the URL itself
                        // handed out a direct download, which is the opposite of the
                        // requirement that videos cannot be downloaded - and since
                        // replays became HLS manifests it would not have played anyway.
                        <Link
                          to={`/live/${stream.slug}`}
                          className="text-blue-600 hover:text-blue-800 text-sm flex items-center gap-1"
                        >
                          <ExternalLink className="h-3 w-3" /> Watch
                        </Link>
                      ) : (
                        <span className="text-gray-400 text-sm">No recording</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={() => handleDeleteStream(stream.id, stream.title)}
                        disabled={deletingStreamId === stream.id}
                        className="flex items-center gap-1 px-2 py-1 text-red-600 hover:text-red-800 hover:bg-red-50 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Delete stream"
                      >
                        {deletingStreamId === stream.id ? (
                          <div className="animate-spin rounded-full h-4 w-4 border-2 border-red-600 border-t-transparent"></div>
                        ) : (
                          <Trash2 className="h-4 w-4" />
                        )}
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Go Live Modal */}
      {showGoLiveModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <Radio className="h-5 w-5 text-red-500 animate-pulse" />
                Go Live Now
              </h3>
              <button
                onClick={() => setShowGoLiveModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                ×
              </button>
            </div>
            
            <form onSubmit={(e) => { e.preventDefault(); handleGoLive(); }} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Stream Title *
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Enter your stream title..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description (optional)
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Tell viewers what this stream is about..."
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="golive-age-rating">
                  Age rating
                </label>
                <select
                  id="golive-age-rating"
                  value={formData.ageRating}
                  onChange={(e) => setFormData({ ...formData, ageRating: e.target.value as AgeRatingValue })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                >
                  {AGE_RATINGS.map((rating) => (
                    <option key={rating} value={rating}>{rating}</option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-gray-500">
                  Required. A stream with no rating is treated as adults-only, so nobody
                  would be able to watch it.
                </p>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                <p className="text-sm text-yellow-800 flex items-start gap-2">
                  <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  Once you go live, your stream will be visible to all users immediately.
                </p>
              </div>
              
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowGoLiveModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {isSubmitting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                      Starting...
                    </>
                  ) : (
                    <>
                      <Radio className="h-4 w-4" />
                      Go Live
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Schedule Stream Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <Calendar className="h-5 w-5 text-blue-500" />
                Schedule Live Stream
              </h3>
              <button
                onClick={() => setShowCreateModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                ×
              </button>
            </div>
            
            <form onSubmit={handleCreateStream} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Stream Title *
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Enter your stream title..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description (optional)
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Tell viewers what this stream is about..."
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="create-age-rating">
                  Age rating
                </label>
                <select
                  id="create-age-rating"
                  value={formData.ageRating}
                  onChange={(e) => setFormData({ ...formData, ageRating: e.target.value as AgeRatingValue })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  {AGE_RATINGS.map((rating) => (
                    <option key={rating} value={rating}>{rating}</option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-gray-500">
                  Required. A stream with no rating is treated as adults-only, so nobody
                  would be able to watch it.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Scheduled Start Time (optional)
                </label>
                <input
                  type="datetime-local"
                  value={formData.scheduledStartTime}
                  onChange={(e) => setFormData({ ...formData, scheduledStartTime: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Leave empty to create a stream you can start anytime
                </p>
              </div>
              
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {isSubmitting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                      Creating...
                    </>
                  ) : (
                    <>
                      <Plus className="h-4 w-4" />
                      Create Stream
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {streamToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <Trash2 className="h-5 w-5 text-red-500" />
                Delete Stream
              </h3>
            </div>
            
            <div className="p-6">
              <p className="text-gray-600 mb-4">
                Are you sure you want to delete <span className="font-semibold text-gray-900">"{streamToDelete.title}"</span>?
              </p>
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-6">
                <p className="text-sm text-red-800 flex items-start gap-2">
                  <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  This action cannot be undone. All stream data, including chat messages and analytics, will be permanently deleted.
                </p>
              </div>
              
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setStreamToDelete(null)}
                  disabled={deletingStreamId === streamToDelete.id}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={confirmDeleteStream}
                  disabled={deletingStreamId === streamToDelete.id}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {deletingStreamId === streamToDelete.id ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                      Deleting...
                    </>
                  ) : (
                    <>
                      <Trash2 className="h-4 w-4" />
                      Delete Stream
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Hidden file input for thumbnail upload */}
      <input
        type="file"
        ref={thumbnailInputRef}
        onChange={handleThumbnailUpload}
        accept="image/*"
        className="hidden"
      />
    </div>
  );
};

export default LiveStreamManagement;
